import React, { useState } from 'react';
import { X, Eye, EyeOff, Loader2, Check, AlertCircle } from 'lucide-react';
import { api, errorMessage } from '../lib/api';

const strength = (p) => {
  if (!p) return { label: '', pct: 0, color: 'bg-white/10' };
  let score = 0;
  if (p.length >= 8) score++;
  if (p.length >= 12) score++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) score++;
  if (/\d/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;
  const levels = [
    { label: 'Too short', pct: 15, color: 'bg-red-500' },
    { label: 'Weak',      pct: 30, color: 'bg-red-500' },
    { label: 'Fair',      pct: 55, color: 'bg-amber-500' },
    { label: 'Good',      pct: 75, color: 'bg-emerald-400' },
    { label: 'Strong',    pct: 100, color: 'bg-emerald-400' },
  ];
  return levels[Math.min(score, levels.length - 1)];
};

const PasswordInput = ({ value, onChange, placeholder, testId, autoFocus }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        data-testid={testId}
        className="w-full h-11 pl-3 pr-11 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        data-testid={`${testId}-toggle`}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white"
        tabIndex={-1}
      >
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
};

export const ChangePasswordDialog = ({ open, onClose }) => {
  const [cur, setCur] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);
  const s = strength(next);

  const close = () => {
    setCur(''); setNext(''); setConfirm(''); setErr(''); setDone(false); setSaving(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!cur) { setErr('Please enter your current password'); return; }
    if (next.length < 8) { setErr('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setErr('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: cur, new_password: next });
      setDone(true);
      setTimeout(close, 1200);
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not change password'));
    }
    setSaving(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={close}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[#0f0f14] border border-white/10 p-6 my-4" data-testid="change-password-dialog">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-emerald-300">Account</div>
            <h2 className="mt-1 text-xl font-semibold text-white">Change password</h2>
          </div>
          <button type="button" onClick={close} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {done ? (
          <div className="flex items-center gap-2 text-emerald-300 py-6"><Check className="w-4 h-4" /> Password updated.</div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Current password</label>
              <div className="mt-1.5"><PasswordInput value={cur} onChange={setCur} placeholder="Enter current password" testId="cp-current" autoFocus /></div>
            </div>
            <div>
              <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">New password</label>
              <div className="mt-1.5"><PasswordInput value={next} onChange={setNext} placeholder="At least 8 characters" testId="cp-new" /></div>
              {next && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden"><div className={`h-full ${s.color}`} style={{ width: `${s.pct}%` }} /></div>
                  <span className="text-[11px] text-white/50">{s.label}</span>
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Confirm new password</label>
              <div className="mt-1.5"><PasswordInput value={confirm} onChange={setConfirm} placeholder="Repeat new password" testId="cp-confirm" /></div>
            </div>
            {err && <div className="flex items-start gap-2 text-[13px] text-red-300"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span></div>}
            <button type="submit" disabled={saving} data-testid="cp-submit" className="w-full h-11 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update password'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export const AdminResetPasswordDialog = ({ open, onClose, user }) => {
  const [pwd, setPwd] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState(null);
  const s = strength(pwd);

  const close = () => {
    setPwd(''); setErr(''); setResult(null); setSaving(false); setNotify(true);
    onClose();
  };

  const generate = () => {
    // 14-char URL-safe-ish strong password
    const alphabet = 'ABCDEFGHJKMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    const arr = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    const out = Array.from(arr, (n) => alphabet[n % alphabet.length]).join('');
    setPwd(out);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (pwd.length < 8) { setErr('Password must be at least 8 characters'); return; }
    setSaving(true);
    try {
      const r = await api.post(`/users/${user.id}/reset-password`, { new_password: pwd, email_user: notify });
      setResult(r.data);
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not reset password'));
    }
    setSaving(false);
  };

  if (!open || !user) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={close}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[#0f0f14] border border-white/10 p-6 my-4" data-testid="admin-reset-dialog">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-amber-300">Admin</div>
            <h2 className="mt-1 text-xl font-semibold text-white">Reset password</h2>
            <p className="mt-1 text-[13px] text-white/55">For <span className="text-white/80">{user.full_name}</span> ({user.email})</p>
          </div>
          <button type="button" onClick={close} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        {result ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-300"><Check className="w-4 h-4" /> Password updated.</div>
            {notify && (
              <div className="text-[13px] text-white/70">
                {result.email?.sent ? 'The new password was emailed to the user.' :
                 result.email?.mocked ? 'Email delivery is in mocked mode (EMERGENT_EMAIL_KEY missing). The user was not emailed.' :
                 'Email delivery failed — please share the new password manually.'}
              </div>
            )}
            <button type="button" onClick={close} className="w-full h-10 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold">Done</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">New password</label>
                <button type="button" onClick={generate} data-testid="admin-reset-generate" className="text-[11px] tracking-[0.14em] uppercase font-semibold text-cyan-300 hover:text-cyan-200">Generate</button>
              </div>
              <PasswordInput value={pwd} onChange={setPwd} placeholder="At least 8 characters" testId="admin-reset-pwd" autoFocus />
              {pwd && (
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-white/5 overflow-hidden"><div className={`h-full ${s.color}`} style={{ width: `${s.pct}%` }} /></div>
                  <span className="text-[11px] text-white/50">{s.label}</span>
                </div>
              )}
            </div>
            <label className="flex items-center gap-2.5 text-[13px] text-white/75 cursor-pointer select-none">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="admin-reset-notify" className="h-4 w-4 accent-cyan-400" />
              Email the new password to the user
            </label>
            {err && <div className="flex items-start gap-2 text-[13px] text-red-300"><AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" /><span>{err}</span></div>}
            <button type="submit" disabled={saving} data-testid="admin-reset-submit" className="w-full h-11 rounded-lg bg-amber-400 hover:bg-amber-300 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export const ForgotPasswordDialog = ({ open, onClose, defaultEmail = '' }) => {
  const [email, setEmail] = useState(defaultEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const close = () => {
    setEmail(defaultEmail); setSent(false); setSending(false);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/auth/forgot-password', { email: email.trim() });
    } catch (ex) { /* silent — never reveal */ }
    setSent(true);
    setSending(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={close}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-[#0f0f14] border border-white/10 p-6 my-4" data-testid="forgot-password-dialog">
        <div className="flex items-start justify-between mb-5">
          <div>
            <div className="text-[10px] tracking-[0.22em] font-semibold uppercase text-cyan-300">Account</div>
            <h2 className="mt-1 text-xl font-semibold text-white">Forgot your password?</h2>
            <p className="mt-1 text-[13px] text-white/55">We'll email you a link to reset it (valid for 30 minutes).</p>
          </div>
          <button type="button" onClick={close} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        {sent ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-300"><Check className="w-4 h-4" /> If an account exists for that email, a reset link is on its way.</div>
            <button type="button" onClick={close} className="w-full h-10 rounded-lg bg-white/10 hover:bg-white/15 text-white font-semibold">Close</button>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <label className="text-[11px] tracking-[0.18em] font-semibold uppercase text-white/60">Email</label>
              <input
                type="email"
                required
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                data-testid="fp-email"
                className="mt-1.5 w-full h-11 px-3 rounded-lg bg-white/[0.03] border border-white/10 text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-400/50"
              />
            </div>
            <button type="submit" disabled={sending || !email} data-testid="fp-submit" className="w-full h-11 rounded-lg bg-cyan-400 hover:bg-cyan-300 text-black font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send reset link'}
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default PasswordInput;
